import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

import { API_URL, apiFetch } from '../config/api';

// ── TypewriterEffect Component ──────────────────────────────────────────────
function TypewriterEffect({ text, speed = 50, onComplete }: { text: string; speed?: number; onComplete?: () => void }) {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    if (displayedText.length < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText(text.slice(0, displayedText.length + 1));
      }, speed);
      return () => clearTimeout(timer);
    } else if (displayedText.length === text.length && onComplete) {
      onComplete();
    }
  }, [displayedText, text, speed, onComplete]);

  return <span>{displayedText}</span>;
}

// ── DNAReveal Component ────────────────────────────────────────────────────
function DNAReveal({ dnaId, onComplete }: { dnaId: string; onComplete: () => void }) {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setRevealed(true);
      setTimeout(onComplete, 1000);
    }, 2000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div style={{
      textAlign: 'center',
      animation: 'fadeIn 0.6s ease-out'
    }}>
      <div style={{
        fontSize: '48px',
        fontWeight: 800,
        marginBottom: '24px',
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        opacity: revealed ? 1 : 0.3,
        transform: revealed ? 'scale(1)' : 'scale(0.8)',
        transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>
        🧬
      </div>
      <h2 style={{
        fontSize: '28px',
        fontWeight: 700,
        marginBottom: '16px',
        color: '#fff'
      }}>
        Your DNA ID
      </h2>
      <div style={{
        fontSize: '20px',
        fontFamily: 'monospace',
        fontWeight: 600,
        color: '#6366f1',
        letterSpacing: '2px',
        padding: '16px',
        background: 'rgba(99, 102, 241, 0.1)',
        borderRadius: '12px',
        border: '2px solid rgba(99, 102, 241, 0.3)',
        marginBottom: '24px',
        opacity: revealed ? 1 : 0,
        transform: revealed ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 0.6s ease-out',
      }}>
        {dnaId}
      </div>
      <p style={{
        color: 'var(--text-tertiary)',
        fontSize: '14px',
        lineHeight: '1.6',
        maxWidth: '400px',
        margin: '0 auto'
      }}>
        This is your unique Digital Twin identifier. Keep it safe — it's your connection to your AI self.
      </p>
    </div>
  );
}

// ── AnneFilizIntro Component ───────────────────────────────────────────────
function AnneFilizIntro({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (step === 0) {
      const timer = setTimeout(() => setStep(1), 1500);
      return () => clearTimeout(timer);
    }
  }, [step]);

  return (
    <div style={{
      textAlign: 'center',
      animation: 'fadeIn 0.6s ease-out'
    }}>
      <div style={{
        fontSize: '64px',
        marginBottom: '24px',
        animation: 'float 3s ease-in-out infinite',
      }}>
        👑
      </div>
      <h2 style={{
        fontSize: '28px',
        fontWeight: 700,
        marginBottom: '12px',
        color: '#fff'
      }}>
        Meet Anne Filiz
      </h2>
      <p style={{
        color: 'var(--text-tertiary)',
        fontSize: '16px',
        lineHeight: '1.6',
        maxWidth: '400px',
        margin: '0 auto 32px'
      }}>
        {step === 0 ? (
          <TypewriterEffect
            text="I'm your guide in this journey. I'll help you understand your Digital Twin and nurture its growth."
            speed={40}
            onComplete={() => setStep(1)}
          />
        ) : (
          "I'm your guide in this journey. I'll help you understand your Digital Twin and nurture its growth."
        )}
      </p>
      {step === 1 && (
        <button
          onClick={onComplete}
          style={{
            padding: '12px 32px',
            background: 'linear-gradient(to right, #6366f1, #8b5cf6)',
            borderRadius: '8px',
            border: 'none',
            color: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 10px 20px rgba(99, 102, 241, 0.3)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          Continue
        </button>
      )}
    </div>
  );
}

// ── OnboardingQuestion Component ───────────────────────────────────────────
interface Question {
  id: number;
  text: string;
  text_en: string;
  category: string;
  subcategory: string;
  follow_up: string;
}

function OnboardingQuestion({ question, onAnswer }: { question: Question; onAnswer: (answer: string) => void }) {
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (answer.trim()) {
      setSubmitted(true);
      setTimeout(() => onAnswer(answer), 600);
    }
  };

  return (
    <div style={{
      animation: 'fadeIn 0.6s ease-out'
    }}>
      <div style={{
        fontSize: '32px',
        marginBottom: '24px',
        textAlign: 'center'
      }}>
        ❓
      </div>
      <h3 style={{
        fontSize: '20px',
        fontWeight: 600,
        marginBottom: '24px',
        color: '#fff',
        lineHeight: '1.6'
      }}>
        {question.text_en || question.text}
      </h3>
      <textarea
        value={answer}
        onChange={e => setAnswer(e.target.value)}
        placeholder="Share your thoughts..."
        style={{
          width: '100%',
          minHeight: '100px',
          padding: '16px',
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          color: '#fff',
          fontSize: '14px',
          fontFamily: 'Inter, system-ui, sans-serif',
          resize: 'none',
          marginBottom: '16px',
          transition: 'all 0.2s',
        }}
        onFocus={e => {
          e.currentTarget.style.borderColor = '#6366f1';
          e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)';
        }}
        onBlur={e => {
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
        }}
      />
      <button
        onClick={handleSubmit}
        disabled={!answer.trim() || submitted}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: answer.trim() ? 'linear-gradient(to right, #6366f1, #8b5cf6)' : 'rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          border: 'none',
          color: '#fff',
          fontWeight: 600,
          cursor: answer.trim() ? 'pointer' : 'not-allowed',
          fontSize: '14px',
          opacity: submitted ? 0.7 : 1,
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => {
          if (answer.trim()) {
            e.currentTarget.style.transform = 'translateY(-2px)';
          }
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {submitted ? 'Saving...' : 'Next'}
      </button>
    </div>
  );
}

// ── Main Onboarding Component ─────────────────────────────────────────────

export default function Onboarding() {
  const { user, reloadUser } = useAuth();
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────
  const [step, setStep] = useState<'birth' | 'dna' | 'anne' | 'questions' | 'complete'>('birth');
  const [dnaId, setDnaId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Check if already onboarded ─────────────────────────────────────────
  useEffect(() => {
    if (user && user.work_role && !['user', 'none', ''].includes(String(user.work_role))) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // ── Step 1: Birth — Twin oluştur ────────────────────────────────────────
  const handleBirth = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_URL}/api/twin/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user?.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create twin');

      setDnaId(data.dna_id);
      setStep('dna');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: DNA Reveal ─────────────────────────────────────────────────
  const handleDNAComplete = () => {
    setStep('anne');
  };

  // ── Step 3: Anne Filiz Intro ───────────────────────────────────────────
  const handleAnneComplete = async () => {
    setLoading(true);
    setError(null);
    try {
      // 3 soru çek
      const res = await apiFetch(`${API_URL}/api/twin/ask-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ user_id: user?.id }),
      });
      const data = await res.json();
      if (data.question) {
        setQuestions([data.question]);
        setCurrentQuestionIdx(0);
        setAnswers([]);
        setStep('questions');
      } else {
        setStep('complete');
      }
    } catch (e) {
      console.error('Failed to fetch questions:', e);
      setStep('complete');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 4: Questions ──────────────────────────────────────────────────
  const handleQuestionAnswer = async (answer: string) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);

    // Sonraki soruyu çek
    if (newAnswers.length < 3) {
      try {
        const res = await apiFetch(`${API_URL}/api/twin/ask-question`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ user_id: user?.id }),
        });
        const data = await res.json();
        if (data.question) {
          setQuestions([...questions, data.question]);
          setCurrentQuestionIdx(newAnswers.length);
        } else {
          setStep('complete');
        }
      } catch (e) {
        console.error('Failed to fetch next question:', e);
        setStep('complete');
      }
    } else {
      setStep('complete');
    }
  };

  // ── Step 5: Complete ───────────────────────────────────────────────────
  const handleComplete = async () => {
    setLoading(true);
    try {
      await apiFetch(`${API_URL}/user/onboarding-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user?.id, role: 'os_user', package_slug: 'os_free', cloud_provider: 'cloudflare' }),
      });
      await reloadUser();
      navigate('/dashboard', { replace: true });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(circle at top right, #1e1b4b, #0f172a)',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Inter, system-ui, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        background: 'rgba(30, 41, 59, 0.7)',
        backdropFilter: 'blur(16px)',
        borderRadius: '24px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '40px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        {error && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            color: '#fca5a5',
            fontSize: '14px',
            marginBottom: '24px'
          }}>
            {error}
          </div>
        )}

        {step === 'birth' && (
          <div style={{ textAlign: 'center', animation: 'fadeIn 0.6s ease-out' }}>
            <div style={{
              fontSize: '64px',
              marginBottom: '24px',
              animation: 'pulse 2s ease-in-out infinite',
            }}>
              🌱
            </div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 800,
              marginBottom: '12px',
            }}>
              Welcome, {user?.display_name?.split(' ')[0]}!
            </h1>
            <p style={{
              color: 'var(--text-tertiary)',
              fontSize: '16px',
              lineHeight: '1.6',
              marginBottom: '32px',
            }}>
              Your Digital Twin is about to be born. This AI will learn from you, grow with you, and become your perfect digital companion.
            </p>
            <button
              onClick={handleBirth}
              disabled={loading}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(to right, #6366f1, #8b5cf6)',
                borderRadius: '12px',
                border: 'none',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: '16px',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Creating Twin...' : '🧬 Birth Your Twin'}
            </button>
          </div>
        )}

        {step === 'dna' && dnaId && (
          <DNAReveal dnaId={dnaId} onComplete={handleDNAComplete} />
        )}

        {step === 'anne' && (
          <AnneFilizIntro onComplete={handleAnneComplete} />
        )}

        {step === 'questions' && questions.length > 0 && (
          <div>
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '32px',
            }}>
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} style={{
                  flex: 1,
                  height: '4px',
                  borderRadius: '2px',
                  background: i <= currentQuestionIdx ? '#6366f1' : 'rgba(255,255,255,0.1)',
                  transition: 'all 0.3s'
                }} />
              ))}
            </div>
            <OnboardingQuestion
              question={questions[currentQuestionIdx]}
              onAnswer={handleQuestionAnswer}
            />
          </div>
        )}

        {step === 'complete' && (
          <div style={{ textAlign: 'center', animation: 'fadeIn 0.6s ease-out' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(16, 185, 129, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#10b981',
              margin: '0 auto 24px',
              fontSize: '40px',
            }}>
              ✨
            </div>
            <h2 style={{
              fontSize: '28px',
              fontWeight: 700,
              marginBottom: '12px',
            }}>
              Your Twin is Born!
            </h2>
            <p style={{
              color: 'var(--text-tertiary)',
              fontSize: '14px',
              lineHeight: '1.6',
              marginBottom: '32px',
            }}>
              {dnaId && (
                <>
                  DNA ID: <span style={{ color: '#6366f1', fontFamily: 'monospace' }}>{dnaId}</span>
                  <br />
                  <br />
                </>
              )}
              Your Digital Twin is ready to learn and grow. Let's enter the AI Operating System.
            </p>
            <button
              onClick={handleComplete}
              disabled={loading}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(to right, #6366f1, #8b5cf6)',
                borderRadius: '12px',
                border: 'none',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: '16px',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Entering...' : '🚀 Launch RunAI OS'}
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
}
